import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { OnboardingState } from '@ekhodealer/ekho-common';
import { getInvitationErrorMessage, useAuth } from '@ekhodealer/ekho-common/auth-client';
import { GuardId, useGuardLoadingState } from '@ekhodealer/ekho-common/components';
import { Button, Card } from '@ekhodealer/ekho-common/rev';

import type { AdminGetMeResponse } from '@ekhodealer/ekho-common';
import type { ReactNode } from 'react';

import { getAuthFlowTarget } from '@common/lib/authRouting';
import { paths } from '@config/paths';

interface InviteAuthGuardProps {
	children: ReactNode;
}

/**
 * InviteAuthGuard
 * - Intended for /setup/* flows (MFA challenge is now part of /login)
 * - If user already signed in: allow
 * - If not signed in: requires invite/email URL params to sign in
 *
 * Note: AuthSessionProvider handles Firebase auth listening and /me fetching.
 * This guard reads auth state from atoms and handles invite-based sign-in.
 */
export function InviteAuthGuard({ children }: InviteAuthGuardProps) {
	// me is now provided by AuthSessionProvider via authStateAtom (admin /me endpoint)
	const {
		isAuthenticated,
		isLoading,
		user,
		me,
		signIn,
		signInWithCustomToken,
		error,
		clearError,
	} = useAuth();
	const adminMe = me as AdminGetMeResponse | null;
	const [searchParams] = useSearchParams();
	const [hasAttempted, setHasAttempted] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	// Keep global loading overlay in sync
	// Wait for auth state to settle AND me to be loaded (when authenticated)
	const isGuardLoading = isLoading || !hasAttempted || (isAuthenticated && !adminMe);
	useGuardLoadingState(GuardId.AUTH, isGuardLoading);

	const inviteToken = searchParams.get('invite');
	const email = searchParams.get('email');
	const customToken = searchParams.get('customToken');

	useEffect(() => {
		const attempt = async () => {
			// Already signed in (or already tried)
			if (isAuthenticated || user || hasAttempted) {
				setHasAttempted(true);
				return;
			}

			// Need at least one auth path
			if (!email || (!inviteToken && !customToken)) {
				setHasAttempted(true);
				return;
			}

			clearError();
			try {
				if (inviteToken && email) {
					try {
						const result = await signIn({ email, password: inviteToken });

						// If MFA is required, redirect to /login where MfaChallengeForm will render
						if (!result.success && result.mfaRequired) {
							navigate(paths.login.getHref(), { replace: true });
							return;
						}
					} catch {
						// Invite token may be stale (password was changed during profile setup).
						// Fall back to customToken if available — the verification email link embeds
						// one specifically for this new-browser-session scenario.
						if (customToken) {
							await signInWithCustomToken({ token: customToken });
						} else {
							throw new Error(
								'Failed to sign in with invite token and no valid custom token available.',
							);
						}
					}
				} else if (customToken) {
					// signInWithCustomToken does not trigger an MFA challenge, but that is
					// acceptable here: users at the verify-email step haven't enrolled MFA yet.
					// checkAndSetMfaEnrollmentStatus in useAuth sets needsMfaEnrollment=true,
					// so they will be directed to /setup/mfa after verification as expected.
					await signInWithCustomToken({ token: customToken });
				}
			} catch {
				// error is stored in atoms; UI below will render message
			} finally {
				setHasAttempted(true);
			}
		};

		if (!isLoading) {
			attempt();
		}
	}, [
		isAuthenticated,
		user,
		hasAttempted,
		inviteToken,
		email,
		customToken,
		clearError,
		signIn,
		signInWithCustomToken,
		isLoading,
		navigate,
	]);

	// Once signed in and me data is available, route user to the correct invite onboarding step.
	// Skip this when on dealer onboarding path (/:id/onboarding); that flow is driven by
	// servicesProvider.onboardingCompleted, not me.onboardingState. Redirecting to home here
	// when me is complete caused a redirect loop with useOnboardingRedirect (orders ↔ onboarding).
	useEffect(() => {
		// Wait for authentication and me data to be ready
		if (!isAuthenticated || !user || !adminMe) {
			return;
		}

		// Dealer onboarding path: /:servicesProviderId/onboarding — do not redirect based on me
		const isDealerOnboardingPath =
			location.pathname.endsWith('/onboarding') &&
			location.pathname.split('/').filter(Boolean).length === 2;
		if (isDealerOnboardingPath) {
			return;
		}

		const target = getAuthFlowTarget(adminMe.onboardingState);

		// Only navigate if we're not already at the target path
		// This prevents redirect loops when user is already at the correct setup step
		// Use exact comparison since startsWith('/') is always true for all paths
		if (location.pathname !== target.path) {
			// Preserve email and invite params for reload support
			const queryString = searchParams.toString();
			const targetPath = queryString ? `${target.path}?${queryString}` : target.path;
			navigate(targetPath, { replace: true });
		}
	}, [isAuthenticated, user, adminMe, navigate, searchParams, location.pathname]);

	// CXP-2691: If user is signed in with matching email and fully onboarded,
	// redirect to home (orders) instead of showing the stale invite error page.
	// Skip dealer onboarding path to avoid redirect loop with useOnboardingRedirect.
	const isDealerOnboardingPath =
		location.pathname.endsWith('/onboarding') &&
		location.pathname.split('/').filter(Boolean).length === 2;

	if (isAuthenticated && hasAttempted && user && adminMe && email && !isDealerOnboardingPath) {
		if (
			user.email?.toLowerCase() === email.toLowerCase() &&
			adminMe.onboardingState === OnboardingState.COMPLETE
		) {
			navigate(paths.home.getHref(), { replace: true });
			return null;
		}
	}

	// If not authenticated after attempting, redirect to /no-access
	if (!isAuthenticated && hasAttempted) {
		const missingParams = !email || (!inviteToken && !customToken);
		if (missingParams) {
			navigate(paths.noAccess.getHref(), { replace: true });
			return null;
		}

		// Use invitation-specific error messages (not login messages)
		const errorMessage = error?.code
			? getInvitationErrorMessage(error.code)
			: 'Your invitation is invalid or has expired.';

		return (
			<Card>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					<h3>Invite Link Required</h3>
					<p>{errorMessage}</p>
					<Button onPress={() => window.location.reload()}>Try Again</Button>
				</div>
			</Card>
		);
	}

	// While waiting for auth state or me data to settle, render nothing (global LoadingPage handles UI)
	if (!isAuthenticated || (isAuthenticated && !adminMe)) {
		return null;
	}

	return <>{children}</>;
}

export default InviteAuthGuard;
